// apps/admin-ui/src/components/widgets/DexOnRampActivity.tsx
// DEX On-Ramp (Crypto Funding) activity widget
// ALL RIGHTS RESERVED

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface DexOnRampOrder {
  id: string;
  userId: string;
  userEmail?: string;
  status: string;
  fromToken: string;
  fromAmount: string;
  cxxrCredited: string;
  networkMode: 'mainnet' | 'testnet';
  createdAt: string;
}

interface DexOnRampStats {
  pending: number;
  submitted: number;
  completed: number;
  failed: number;
  total: number;
  totalVolume: number;
  recentOrders: DexOnRampOrder[];
}

// Use same port as exchange service
const EXCHANGE_URL = import.meta.env.VITE_EXCHANGE_URL || 'http://localhost:3009';

export function DexOnRampActivity() {
  const [stats, setStats] = useState<DexOnRampStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${EXCHANGE_URL}/api/v1/admin/dex/onramp/stats`);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch DEX onramp stats:', err);
      setError(err.message || 'Failed to load');
      // Use mock data for development
      setStats({
        pending: 1,
        submitted: 0,
        completed: 2,
        failed: 0,
        total: 3,
        totalVolume: 432.42,
        recentOrders: [
          {
            id: '3838978000001',
            userId: '1',
            userEmail: 'alice1@test.com',
            status: 'completed',
            fromToken: 'WETH',
            fromAmount: '0.1',
            cxxrCredited: '216.21',
            networkMode: 'testnet',
            createdAt: new Date().toISOString(),
          },
        ],
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(num);
  };

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#22c55e';
      case 'failed':
      case 'mint_failed': return '#ef4444';
      default: return '#f59e0b';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        Loading DEX OnRamp activity...
      </div>
    );
  }

  if (!stats) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-error)' }}>Failed to load DEX OnRamp data</p>
        <button onClick={fetchStats} style={{ marginTop: '0.5rem', padding: '0.25rem 0.5rem' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Stats Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(4, 1fr)', 
        gap: '0.75rem', 
        marginBottom: '1rem' 
      }}>
        <div style={{ 
          textAlign: 'center', 
          padding: '0.75rem', 
          background: 'rgba(251, 191, 36, 0.1)', 
          borderRadius: '8px' 
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>
            {stats.pending + stats.submitted}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Processing</div>
        </div>
        <div style={{ 
          textAlign: 'center', 
          padding: '0.75rem', 
          background: 'rgba(34, 197, 94, 0.1)', 
          borderRadius: '8px' 
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#22c55e' }}>
            {stats.completed}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Completed</div>
        </div>
        <div style={{ 
          textAlign: 'center', 
          padding: '0.75rem', 
          background: 'rgba(239, 68, 68, 0.1)', 
          borderRadius: '8px' 
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#ef4444' }}>
            {stats.failed}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Failed</div>
        </div>
        <div style={{ 
          textAlign: 'center', 
          padding: '0.75rem', 
          background: 'rgba(59, 130, 246, 0.1)', 
          borderRadius: '8px' 
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>
            {formatCurrency(stats.totalVolume)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Total Volume</div>
        </div>
      </div>

      {/* Recent Orders */}
      {stats.recentOrders && stats.recentOrders.length > 0 && (
        <div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '0.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}>
            <span>Recent Activity</span>
            <Link to="/admin/dex/onramps" style={{ fontSize: '0.75rem', color: 'var(--color-primary)' }}>
              View All →
            </Link>
          </div>
          <table style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>User</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>From</th>
                <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>CXXR</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '0.5rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Network</th>
                <th style={{ textAlign: 'right', padding: '0.5rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentOrders.slice(0, 5).map((order) => (
                <tr key={order.id} style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                  <td style={{ padding: '0.5rem' }}>{order.userEmail || `User ${order.userId}`}</td>
                  <td style={{ padding: '0.5rem' }}>{order.fromAmount} {order.fromToken}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: '#22c55e' }}>
                    {formatCurrency(order.cxxrCredited)}
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    <span style={{ color: getStatusColor(order.status), fontWeight: 500, textTransform: 'capitalize' }}>
                      {order.status}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem' }}>
                    <span style={{ 
                      padding: '0.125rem 0.375rem',
                      borderRadius: '4px',
                      fontSize: '0.625rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      background: order.networkMode === 'testnet' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                      color: order.networkMode === 'testnet' ? '#b45309' : '#15803d',
                    }}>
                      {order.networkMode}
                    </span>
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--color-text-secondary)' }}>
                    {formatTime(order.createdAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        marginTop: '0.75rem', 
        paddingTop: '0.75rem', 
        borderTop: '1px solid var(--color-border)',
        textAlign: 'center',
        fontSize: '0.75rem',
        color: 'var(--color-text-muted)',
      }}>
        {stats.total} total crypto funding transactions
      </div>
    </div>
  );
}

export default DexOnRampActivity;

